import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { ReactElement, ReactNode } from 'react';
import { IconProps } from '../icons/generated';
import { Stack } from '../stack';
import { breadcrumb_item_base_css, breadcrumb_item_selected_css, breadcrumb_item_disabled_css } from './styles';

export interface BreadcrumbItem {
  label?: ReactNode;
  /** label 좌측 렌더링될 Icon (size, color 고정) */
  leftIcon?: ReactElement<IconProps>;
  href?: string;
  disabled?: boolean;
  selected?: boolean;
  /** 외부 제어 필요할 경우 사용 */
  onClick?: () => void;
}

export interface BreadcrumbProps {
  className?: string;
  items: BreadcrumbItem[];
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ className, items }) => {
  if (items.some(({ label, leftIcon }) => !label && !leftIcon))
    throw new Error('item은 label 또는 leftIcon이 존재해야합니다.');

  return (
    <nav className={classNames('pds-breadcrumb', className)} aria-label='Breadcrumb'>
      <BreadcrumbList>
        {items.map(({ label, leftIcon, href, selected, disabled, onClick }, index) => {
          const renderLabel = leftIcon ? (
            <Stack direction='row' gap={4} align='center' key={index}>
              {React.cloneElement(leftIcon, {
                size: 16,
                className: 'pds-breadcrumb-icon',
              })}
              {label}
            </Stack>
          ) : (
            label
          );
          return (
            <React.Fragment key={index}>
              <BreadcrumbItem selected={selected} disabled={disabled}>
                {!!href ? (
                  <a className='pds-breadcrumb-link' href={href} onClick={onClick}>
                    {renderLabel}
                  </a>
                ) : (
                  renderLabel
                )}
              </BreadcrumbItem>
              {index < items.length - 1 && <BreadcrumbItem aria-hidden>/</BreadcrumbItem>}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </nav>
  );
};

const BreadcrumbList = styled.ol`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 0;
  padding: 0;
`;

const BreadcrumbItem = styled.li<{ selected?: boolean; disabled?: boolean }>`
  ${breadcrumb_item_base_css};
  ${({ selected }) => selected && breadcrumb_item_selected_css};
  ${({ disabled }) => disabled && breadcrumb_item_disabled_css};
`;
